import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Loader2, CheckCircle, RefreshCw, LogOut } from 'lucide-react';

const VerifyEmailPage = () => {
  const { user, verifyEmail, resendVerification, logout } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const newCode = [...code];
    newCode[index] = value.slice(-1); // only last char
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (codeStr) => {
    const fullCode = codeStr || code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    const result = await verifyEmail(fullCode);
    if (result.success) {
      toast.success('Email verified! Welcome to ReflectLogs');
    } else {
      toast.error(result.error);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    const result = await resendVerification();
    if (result.success) {
      toast.success('New verification code sent to your email');
      setCooldown(60);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } else {
      toast.error(result.error);
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="verify-email-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <Mail size={28} className="text-indigo-600" />
          </div>
          <CardTitle className="text-2xl text-slate-800">Verify Your Email</CardTitle>
          <p className="text-sm text-slate-500 mt-2">
            We've sent a 6-digit code to<br />
            <span className="font-semibold text-indigo-600">{user?.email}</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 6-digit code input */}
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {code.map((digit, i) => (
              <Input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                data-testid={`verify-code-input-${i}`}
                className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-indigo-500 focus:ring-indigo-200"
                disabled={loading}
              />
            ))}
          </div>

          <Button
            onClick={() => handleVerify()}
            disabled={loading || code.some(d => d === '')}
            data-testid="verify-submit-btn"
            className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
          >
            {loading ? (
              <><Loader2 size={18} className="mr-2 animate-spin" /> Verifying...</>
            ) : (
              <><CheckCircle size={18} className="mr-2" /> Verify Email</>
            )}
          </Button>

          <div className="text-center space-y-3">
            <p className="text-sm text-slate-500">Didn't receive the code?</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              data-testid="resend-code-btn"
            >
              {resending ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending...</>
              ) : cooldown > 0 ? (
                `Resend in ${cooldown}s`
              ) : (
                <><RefreshCw size={14} className="mr-1.5" /> Resend Code</>
              )}
            </Button>

            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="verify-logout-btn"
                className="text-slate-400 hover:text-slate-600"
              >
                <LogOut size={14} className="mr-1.5" /> Sign out
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
